import { ChangeEvent, useCallback, useMemo, useState } from "react"
import useFormState from "@/hooks/useFormState"
import {
   userFieldConfig,
   UserFormField,
   initialUserFormData,
} from "@/pages/Settings/config"
import { UserFormData } from "@/pages/Settings/Users/types"
import { UserRole } from "@/types/user"

export function useUserForm(onSave: (data: UserFormData) => void) {
   const {
      state,
      updateField,
      handleChange: handleInputChange,
      resetForm,
   } = useFormState<UserFormData>(initialUserFormData)
   const initialTouched = useMemo<Record<UserFormField, boolean>>(
      () => ({
         firstName: false,
         lastName: false,
         email: false,
         phoneNumber: false,
         role: false,
      }),
      []
   )
   const initialValidations = useMemo<Record<UserFormField, boolean>>(
      () => ({
         firstName: false,
         lastName: false,
         email: false,
         phoneNumber: false,
         role: false,
      }),
      []
   )
   const [validations, setValidations] =
      useState<Record<UserFormField, boolean>>(initialValidations)

   const [errors, setErrors] = useState<Record<UserFormField, string>>(
      {} as Record<UserFormField, string>
   )
   const [touched, setTouched] =
      useState<Record<UserFormField, boolean>>(initialTouched)

   const validateField = useCallback(
      (field: UserFormField, value: string): boolean => {
         return userFieldConfig[field].validate(value)
      },
      []
   )

   const updateFieldValidation = useCallback(
      (field: UserFormField, value: string) => {
         const isValid = validateField(field, value)
         const errorMessage = isValid ? "" : userFieldConfig[field].errorMessage

         setValidations((prev) => ({ ...prev, [field]: isValid }))
         setErrors((prev) => ({ ...prev, [field]: errorMessage }))
         setTouched((prev) => ({ ...prev, [field]: true }))
      },
      [validateField]
   )

   const handleChange = useCallback(
      (e: ChangeEvent<HTMLInputElement>) => {
         const { name, value } = e.target

         if (name === "lastName" && value.startsWith(" ")) {
            return
         }

         if (
            name === "firstName" &&
            value.startsWith(" ") &&
            state[name] === ""
         ) {
            return
         }

         handleInputChange(e)
         updateFieldValidation(name as UserFormField, value)
      },
      [handleInputChange, updateFieldValidation, state]
   )

   const handleRoleChange = useCallback(
      (values: UserRole[]): void => {
         const [selectedRole] = values
         const newRole = selectedRole ?? ""
         updateField("role", newRole)
         updateFieldValidation("role", newRole)
      },
      [updateField, updateFieldValidation]
   )

   const handleBlur = useCallback((field: UserFormField) => {
      setTouched((prev) => ({ ...prev, [field]: true }))
   }, [])

   const isFormValid = useMemo(() => {
      return Object.keys(validations).every((key) => {
         return (
            touched[key as UserFormField] &&
            validations[key as UserFormField] &&
            String(state[key as UserFormField]).trim() !== ""
         )
      })
   }, [validations, touched, state])

   const resetFormState = useCallback(() => {
      resetForm()
      setTouched(initialTouched)
      setValidations(initialValidations)
   }, [resetForm, initialTouched, initialValidations])

   const handleSubmit = useCallback(() => {
      if (isFormValid) {
         onSave(state)
         resetFormState()
      }
   }, [isFormValid, onSave, state, resetFormState])

   return {
      state,
      validations,
      errors,
      touched,
      handleChange,
      handleRoleChange,
      handleBlur,
      isFormValid,
      handleSubmit,
   }
}
